import React from "react"
import { Helmet } from "react-helmet"
import { useLocation } from "@reach/router"
import Layout from "../../../components/Layout"
import BookingWhatsAppSecondaryCta from "../../../components/booking/BookingWhatsAppSecondaryCta"
import BookingTestimonials from "../../../components/booking/BookingTestimonials"
import InternalDestinationsBlock from "../../../components/booking/InternalDestinationsBlock"
import BookingProofBlock from "../../../components/booking/BookingProofBlock"
import StaticAlternates from "../../../components/StaticAlternates"

const CAL_URL =
  process.env.GATSBY_CALL_15MIN_URL ||
  "https://cal.com/gadait-international/15min"

export default function BookHolidayCallFr() {
  const location = useLocation()
  const params = new URLSearchParams(location.search || "")
  const stayBrief = {
    property: params.get("property") || "",
    reference: params.get("ref") || "",
    checkIn: params.get("checkIn") || "",
    checkOut: params.get("checkOut") || "",
    nights: params.get("nights") || "",
    guests: params.get("guests") || "",
    estimate: params.get("estimate") || "",
    currency: params.get("currency") || "EUR",
    services: params.get("services") || "",
  }
  const hasStayBrief = Boolean(
    stayBrief.property ||
      stayBrief.checkIn ||
      stayBrief.checkOut ||
      stayBrief.guests ||
      stayBrief.estimate ||
      stayBrief.services
  )
  const formatMoney = value => {
    const amount = Number(value || 0)
    if (!amount) return ""
    try {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: stayBrief.currency || "EUR",
        maximumFractionDigits: 0,
      }).format(amount)
    } catch {
      return `${amount} ${stayBrief.currency || "EUR"}`
    }
  }
  const calParams = new URLSearchParams()
  if (stayBrief.property) calParams.set("property", stayBrief.property)
  if (stayBrief.reference) calParams.set("reference", stayBrief.reference)
  if (stayBrief.checkIn) calParams.set("checkIn", stayBrief.checkIn)
  if (stayBrief.checkOut) calParams.set("checkOut", stayBrief.checkOut)
  if (stayBrief.guests) calParams.set("guests", stayBrief.guests)
  if (stayBrief.estimate) calParams.set("estimate", formatMoney(stayBrief.estimate))
  if (stayBrief.services) calParams.set("services", stayBrief.services)
  const calUrl = `${CAL_URL}${CAL_URL.includes("?") ? "&" : "?"}${calParams.toString()}`

  return (
    <Layout>
      <StaticAlternates
        locale="fr"
        canonicalPath="/fr/reserver-un-appel/sejour/"
        enPath="/en/book-a-call/holiday/"
        frPath="/fr/reserver-un-appel/sejour/"
      />
      <Helmet>
        <title>Réserver un Appel Séjour | GADAIT International</title>
      </Helmet>
      <section className="bg-[#f5f5f7] px-4 py-8 sm:px-6 sm:py-14 lg:py-16">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[minmax(0,0.86fr)_minmax(0,1.14fr)] lg:items-start">
          <div className="overflow-hidden rounded-[28px] bg-[#050505] p-6 text-white shadow-[0_28px_80px_rgba(15,23,42,0.16)] sm:rounded-[34px] sm:p-10 lg:sticky lg:top-8">
            <p className="text-xs font-semibold uppercase text-white/52">
              Consultation séjour privée
            </p>
            <h1 className="mt-5 max-w-xl text-4xl font-semibold leading-none text-white sm:text-5xl lg:text-6xl xl:text-7xl">
              Réserver une visio Séjour
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-white/76 sm:mt-6 sm:text-lg sm:leading-8">
              Nous confirmons les dates, voyageurs, services et détails pratiques
              avant de construire une proposition séjour sur mesure.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
              {[
                ["15 min", "Brief privé ciblé"],
                ["7/7", "Suivi expert local"],
                ["24h", "Proposition claire"],
              ].map(([value, label]) => (
                <div
                  key={label}
                  className="rounded-[22px] border border-white/12 bg-white/8 p-4 backdrop-blur"
                >
                  <p className="text-2xl font-semibold">{value}</p>
                  <p className="mt-2 text-[11px] font-semibold uppercase leading-5 text-white/50">
                    {label}
                  </p>
                </div>
              ))}
            </div>

            {hasStayBrief && (
              <div className="mt-7 rounded-[24px] border border-white/14 bg-white/10 p-5 backdrop-blur-xl sm:rounded-[26px]">
                <p className="text-[11px] font-semibold uppercase text-white/50">
                  Contexte sauvegardé
                </p>
                <div className="mt-4 space-y-3 text-sm text-white/82">
                  <p>
                    <strong className="text-white">Propriété :</strong>{" "}
                    {stayBrief.property || stayBrief.reference || "À confirmer"}
                  </p>
                  <p>
                    <strong className="text-white">Dates :</strong>{" "}
                    {stayBrief.checkIn || "À confirmer"} →{" "}
                    {stayBrief.checkOut || "À confirmer"}
                  </p>
                  <p>
                    <strong className="text-white">Voyageurs :</strong>{" "}
                    {stayBrief.guests || "À confirmer"}
                    {stayBrief.nights ? ` · ${stayBrief.nights} nuits` : ""}
                  </p>
                  <p>
                    <strong className="text-white">Estimation :</strong>{" "}
                    {formatMoney(stayBrief.estimate) || "À confirmer"}
                  </p>
                  {stayBrief.services && (
                    <p>
                      <strong className="text-white">Concierge :</strong>{" "}
                      {stayBrief.services}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="rounded-[28px] border border-black/8 bg-white p-2 shadow-[0_28px_80px_rgba(15,23,42,0.08)] sm:rounded-[34px] sm:p-4">
              <div className="border-b border-black/8 px-4 py-5 sm:px-6">
                <p className="text-xs font-semibold uppercase text-gray-400">
                  Choisir un créneau
                </p>
                <p className="mt-2 text-base leading-7 text-[#6e6e73] sm:text-[17px]">
                  Le contexte de votre séjour reste attaché au rendez-vous pour
                  démarrer l’échange avec les bons détails.
                </p>
              </div>
              <iframe
                title="Réserver un appel - séjour"
                src={calUrl}
                className="min-h-[680px] w-full md:min-h-[820px]"
                style={{ border: 0 }}
              />
            </div>
            <div className="mt-5">
              <BookingWhatsAppSecondaryCta flow="holiday" />
            </div>
          </div>
        </div>
      </section>
      <BookingTestimonials />
      <InternalDestinationsBlock />
      <BookingProofBlock />
    </Layout>
  )
}
